'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GenericTag } from '@/styles/GenericTag';
import { Download } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useEntityList } from '@/hooks/useEntityList';
import { downloadCsv } from '@/lib/csv';
import { useProject } from '@/components/ProjectSelector';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { budgetStatusOf } from '../lib/budget-lines';
import { fetchBudgetOverviewAll, fetchBudgetOverviewPage, type BudgetOverviewRow, type BudgetOverviewSummary } from '../services/budget-api';

interface BudgetsOverviewProps {
  /** Open a project's Budget (the page switches to the Budget tab on that project). */
  onOpenBudget?: (projectId: string) => void;
}

/** The KPI cards double as filters: clicking one narrows the table. */
type CardFilter = 'all' | 'with_budget' | 'no_budget';

const EMPTY_SUMMARY: BudgetOverviewSummary = { total: 0, with_budget: 0, without_budget: 0, budget_total: 0, committed: 0, actual: 0, remaining: 0, available: 0 };

const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export function BudgetsOverview({ onOpenBudget }: BudgetsOverviewProps) {
  const { companyId } = useUser();
  const { selectedProject } = useProject();
  const [cardFilter, setCardFilter] = useState<CardFilter>('all');

  // One row per PROJECT (its budget, committed, billed, remaining), read from the server: search / sort / paging in the database
  // layer and the KPIs over every matching project, not over the rows that are loaded.
  const list = useEntityList<BudgetOverviewRow, BudgetOverviewRow>({
    entityType: 'budget_overview',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    filters: cardFilter === 'all' ? undefined : { budget: cardFilter },
    defaultSort: { columnKey: 'project_name', direction: 'asc', sortKey: 'project' },
    mapRow: (row) => row,
    fetchPage: fetchBudgetOverviewPage,
  });
  const summary = (list.pageExtra as { summary: BudgetOverviewSummary } | undefined)?.summary ?? EMPTY_SUMMARY;

  const handleExport = async () => {
    try {
      const rows = await fetchBudgetOverviewAll(list.query);
      if (rows.length === 0) {
        alert('No data found to export.');
        return;
      }
      downloadCsv(`budget-overview-${new Date().toISOString().split('T')[0]}.csv`, rows.map((row) => ({
        project: row.project_name,
        has_budget: row.has_budget ? 'yes' : 'no',
        budget: row.budget_total,
        committed: row.committed,
        billed: row.actual,
        remaining: row.remaining,
        available_to_commit: row.available,
        last_modified: row.updated_at ?? '',
      })));
    } catch (error) {
      console.error('Error exporting the budget overview:', error);
      alert('Error exporting the budget overview. Please try again.');
    }
  };

  const money = (value: number, row: BudgetOverviewRow) => (row.has_budget || value !== 0 ? formatCurrency(value) : '-');

  const columns: GenericTableColumn<BudgetOverviewRow>[] = [
    { key: 'project_name', header: 'Project', width: '14rem', sortable: true, sortKey: 'project', render: (row) => row.project_name },
    {
      key: 'budget',
      header: 'Status',
      width: '9rem',
      // the budget's own status (Draft / In Revision / Approved / Cancelled), the same tag as on the Budget page
      render: (row) => (row.budget ? <GenericTag type="status" value={budgetStatusOf(row.budget.status)} /> : <GenericTag type="status" value="not_started">No Budget</GenericTag>),
    },
    { key: 'budget_total', header: 'Budget', width: '9rem', sortable: true, sortKey: 'budget_total', render: (row) => (row.has_budget ? formatCurrency(row.budget_total) : '-') },
    { key: 'committed', header: 'Committed', width: '9rem', sortable: true, sortKey: 'committed', render: (row) => money(row.committed, row) },
    { key: 'actual', header: 'Billed', width: '9rem', sortable: true, sortKey: 'actual', render: (row) => money(row.actual, row) },
    {
      key: 'remaining',
      header: 'Remaining',
      width: '9rem',
      sortable: true,
      sortKey: 'remaining',
      render: (row) => (row.has_budget ? <span className={row.remaining < 0 ? 'text-red-600 font-medium' : undefined}>{formatCurrency(row.remaining)}</span> : '-'),
    },
    { key: 'updated_at', header: 'Last Modified', width: '8rem', sortable: true, sortKey: 'updated_at', render: (row) => formatDate(row.updated_at) },
  ];

  const kpiCard = (key: CardFilter, title: string, value: React.ReactNode, hint?: string) => (
    <button type="button" key={key} onClick={() => setCardFilter(key)} aria-pressed={cardFilter === key} className="text-left">
      <Card className={cardFilter === key ? 'ring-2 ring-blue-500' : undefined}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-black">{value}</div>
          {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
        </CardContent>
      </Card>
    </button>
  );

  const plainCard = (title: string, value: string, hint?: string, negative = false) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${negative ? 'text-red-600' : 'text-black'}`}>{value}</div>
        {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Budget Overview</h2>
          <p className="text-gray-600 text-sm">Every project with its budget, what is committed and what is billed</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchButton onSearch={list.setSearchInput} placeholder="Search" />
          <ThreeDotMenuButton items={[{ label: 'Export', icon: <Download className="w-4 h-4" />, onClick: handleExport }]} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {kpiCard('all', 'Projects', summary.total)}
        {kpiCard('with_budget', 'With Budget', summary.with_budget, `${summary.without_budget} without`)}
        {plainCard('Budget', formatCurrency(summary.budget_total))}
        {plainCard('Committed', formatCurrency(summary.committed), `Billed ${formatCurrency(summary.actual)}`)}
        {plainCard('Remaining', formatCurrency(summary.remaining), `Available to commit ${formatCurrency(summary.available)}`, summary.remaining < 0)}
      </div>

      <GenericTable
        columns={columns}
        {...list.tableProps}
        rowKey={(row) => row.id}
        scrollContainerMaxWidth="calc(100vw - 19.2rem)"
        minTableWidth={1000}
        onRowClick={(row) => onOpenBudget?.(row.project_id)}
      />
    </div>
  );
}
